import i18n from 'i18next';
import find from 'lodash-es/find';
import { toast } from 'sonner';

import { Dialogs } from '@/shared/types/dialog';
import { Entities, Entity } from '@/shared/types/entity';
import { Intents } from '@/shared/types/intent';
import { Node } from '@/shared/types/node';

type Prev = Dialogs | Intents | Entities;

/**
 * Retrieves other items from the same collection except the one specified by queryId.
 *
 * @param {string} category - The category in which to perform the search.
 * @param {string} categoryId - The identifier for items in the category.
 * @param {string} queryId - The ID of the item to exclude from the results.
 * @param {Prev} prev - The previous state from which to search.
 * @returns {Array<object>} - An array of items in the same collection, excluding the specified item.
 */
export const sameCollection = (
  category: string,
  categoryId: string,
  queryId: string,
  prev: Prev
) => {
  const deletedCategoryIndex = prev[`${category}`].findIndex((x) => x[`${categoryId}`] === queryId);
  const collectionOfDeleted = prev[`${category}`][deletedCategoryIndex].collection;
  return prev[`${category}`]
    .filter((x) => x['collection'] === collectionOfDeleted)
    .filter((x) => x[`${categoryId}`] !== queryId);
};

/**
 * Clears from a dialog entities that do not exist in the brain.
 *
 * @param {{ nodes: Node[] }} newDialog - The dialog to be cleared.
 * @param {Entity[]} entities - The brain's entities.
 */
export const clearNewDialog = (newDialog: { nodes: Node[] }, entities: Entity[]) => {
  for (const node of newDialog.nodes) {
    if (node.conditions) {
      for (const condition of node.conditions) {
        if (condition.rules) {
          for (const rule of condition.rules) {
            if (
              rule?.name &&
              rule.name[0] === '@' &&
              !find(entities, { entity: rule?.name?.replace('@', '') })
            ) {
              rule.name = '';
            }
          }
        }
      }
    }
    if (node.requisites) {
      for (const requisite of node.requisites) {
        if (
          requisite?.check_for &&
          requisite.check_for[0] === '@' &&
          !find(entities, { entity: requisite?.check_for?.replace('@', '') })
        ) {
          toast.warning(i18n.t('dialog.question.invalid_entity', { 0: requisite?.check_for }));
          requisite.check_for = undefined;
        }
      }
    }
  }
};
